import { EntityId, Occupation, OccupationSkill, Skill } from '@restworld/utility-types';
import { ENDPOINTS } from '../../endpoints';
import { useMemo } from 'react';
import { Service } from '../base';
import dynamicAxios from '../../utils/axios';
import useServices from '../../hooks/useServices';
import { Id } from '../../utils/common_types';

class ServiceTaxonomy extends Service {
  indexSkills(occupationId?: number) {
    return this.axiosInstance.get<Skill[]>(ENDPOINTS.taxonomy.skill, { params: { occupation_id: occupationId } });
  }
  indexOccupations(domainInterestId?: EntityId) {
    return this.axiosInstance.get<Occupation[]>(ENDPOINTS.taxonomy.occupation, {
      params: { domain_interest_id: domainInterestId },
    });
  }
  indexOccupationSkills(occupationId?: number) {
    return this.axiosInstance.get<OccupationSkill[]>(ENDPOINTS.taxonomy.occupationSkills, {
      params: { occupation_id: occupationId },
    });
  }
  updateOccupationSkill(occupationId: number, skillId: number, data: Partial<OccupationSkill>) {
    return this.axiosInstance.patch<OccupationSkill>(ENDPOINTS.taxonomy.occupationSkills, {
      occupation_id: occupationId,
      skill_id: skillId,
      ...data,
    });
  }

  updateOccupation({ occupationId, params }: { occupationId: number; params: Partial<Occupation> }) {
    return this.axiosInstance.patch<Id>(ENDPOINTS.taxonomy.occupation, {
      ...params,
      ...params.label_it,
      id: occupationId,
    });
  }

  createOccupation({ params }: { params: Partial<Occupation> }) {
    return this.axiosInstance.post<Occupation>(ENDPOINTS.taxonomy.occupation, {
      ...params,
      ...params.label_it,
    });
  }

  createSkill({ params }: { params: Partial<Skill> }) {
    return this.axiosInstance.post<Skill>(ENDPOINTS.taxonomy.skill, {
      ...params,
      ...params.label_it,
    });
  }

  createOccupationSkills({
    occupationSkills,
  }: {
    occupationSkills:
      | {
          occupation_id: number | undefined;
          skill_id: number;
        }[]
      | undefined;
  }) {
    return this.axiosInstance.post<string>(ENDPOINTS.taxonomy.occupationSkills, {
      occupation_skills: occupationSkills,
    });
  }

  deleteOccupationSkill({ occupationSkillId }: { occupationSkillId?: number }) {
    return this.axiosInstance.delete<string>(ENDPOINTS.taxonomy.occupationSkills, {
      params: { id: occupationSkillId },
    });
  }

  updateSkill({ skillId, params }: { skillId: number; params: Partial<Skill> }) {
    return this.axiosInstance.patch<Id>(ENDPOINTS.taxonomy.skill, {
      ...params,
      ...params.label_it,
      id: skillId,
    });
  }

  fetchPopularOccupations(limit?: number) {
    return this.axiosInstance.get<Occupation[]>(ENDPOINTS.taxonomy.popularOccupations, {
      params: { limit },
    });
  }
}

export const createTaxonomyService = ({ baseUrl, jwt }: { baseUrl: string; jwt?: string }) =>
  new ServiceTaxonomy(dynamicAxios(baseUrl, jwt));

export function useTaxonomyService() {
  const { baseUrl, jwt } = useServices();
  return useMemo(() => new ServiceTaxonomy(dynamicAxios(baseUrl, jwt)), [baseUrl, jwt]);
}

export default useTaxonomyService;
